import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  Save, RefreshCw, ChevronDown, ChevronRight, Check,
  Calendar, Layers, BarChart3, Plus, Minus,
  History, Download, Upload, FileSpreadsheet,
  RotateCcw, GitCompare, X, Lock, Unlock,
} from 'lucide-react';
import { Button }    from '../components/ui/button';
import { Input }     from '../components/ui/input';
import { Label }     from '../components/ui/label';
import { Textarea }  from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { aopAPI }   from '../lib/api';
import { cn }        from '../lib/utils';
import { useNumberFormat, applyFormat } from '../context/NumberFormatContext';
import { useClickstream, EVENTS } from '../context/ClickstreamContext';
import { toast }     from 'sonner';
import api           from '../lib/api';

// ─── Searchable select component ─────────────────────────────────────────────

function SearchableSelect({ value, onValueChange, options, placeholder = 'Select…', className }) {
  const [open, setOpen]     = useState(false);
  const [search, setSearch] = useState('');

  const filtered = options.filter(o =>
    o.label.toLowerCase().includes(search.toLowerCase())
  );
  const selected = options.find(o => o.value === value);

  return (
    <Popover open={open} onOpenChange={o => { setOpen(o); if (!o) setSearch(''); }}>
      <PopoverTrigger asChild>
        <button className={cn(
          'flex items-center justify-between h-8 px-3 text-sm rounded-lg border border-slate-200 bg-white hover:bg-slate-50 gap-2 transition-colors',
          className
        )}>
          <span className="truncate text-slate-700">{selected?.label || placeholder}</span>
          <ChevronDown className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="p-0 w-56" align="start">
        <div className="p-2 border-b border-slate-100">
          <Input
            placeholder="Search…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="h-7 text-xs"
            autoFocus
          />
        </div>
        <div className="max-h-64 overflow-y-auto py-1">
          {filtered.length === 0 && (
            <p className="text-xs text-slate-400 text-center py-4">No results</p>
          )}
          {filtered.map(opt => (
            <button
              key={opt.value}
              onClick={() => { onValueChange(opt.value); setOpen(false); setSearch(''); }}
              className={cn(
                'w-full text-left text-sm py-1.5 hover:bg-slate-50 flex items-center justify-between gap-2',
                opt.isParent && 'font-semibold text-slate-800 bg-slate-50/80 border-t border-slate-100 first:border-t-0',
                opt.isChild  && 'text-slate-600',
                value === opt.value && 'bg-primary-50 text-primary-700 font-medium'
              )}
              style={{ paddingLeft: opt.isChild ? '2rem' : '0.75rem', paddingRight: '0.75rem' }}
            >
              <span className="flex items-center gap-1 truncate">
                {opt.isChild && <span className="text-slate-300 flex-shrink-0">↳</span>}
                {opt.label}
              </span>
              {value === opt.value && <Check className="w-3.5 h-3.5 flex-shrink-0 text-primary-600" />}
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

// ─── Time periods ─────────────────────────────────────────────────────────────

const TIME_PERIODS = {
  monthly:     { label: 'Monthly',   periods: 12, format: i => ['Apr-26','May-26','Jun-26','Jul-26','Aug-26','Sep-26','Oct-26','Nov-26','Dec-26','Jan-27','Feb-27','Mar-27'][i] },
  quarterly:   { label: 'Quarterly', periods: 4,  format: i => ['Q1 FY27','Q2 FY27','Q3 FY27','Q4 FY27'][i] },
  half_yearly: { label: '6 Months',  periods: 2,  format: i => i === 0 ? 'H1 FY27' : 'H2 FY27' },
  annually:    { label: 'Annual',    periods: 1,  format: () => 'FY27' },
};

// ─── Channel options (hierarchical, no duplicates) ────────────────────────────

const CHANNEL_OPTIONS = [
  { value: 'all',            label: 'All Channels' },
  { value: 'd2c',            label: 'D2C',           isParent: true },
  { value: 'website',        label: 'Website',        isChild: true },
  { value: 'quick_commerce', label: 'Quick Commerce', isParent: true },
  { value: 'blinkit',        label: 'Blinkit',        isChild: true },
  { value: 'swiggy',         label: 'Swiggy',         isChild: true },
  { value: 'zepto',          label: 'Zepto',          isChild: true },
  { value: 'knot',           label: 'Knot',           isChild: true },
  { value: 'slikk',          label: 'Slikk',          isChild: true },
  { value: 'zilo',           label: 'Zilo',           isChild: true },
  { value: 'marketplaces',   label: 'Marketplaces',   isParent: true },
  { value: 'amazon',         label: 'Amazon',         isChild: true },
  { value: 'myntra',         label: 'Myntra',         isChild: true },
  { value: 'flipkart',       label: 'Flipkart',       isChild: true },
  { value: 'eternz',         label: 'Eternz',         isChild: true },
  { value: 'nykaa',          label: 'Nykaa',          isChild: true },
  { value: 'ajio',           label: 'Ajio',           isChild: true },
  { value: 'meesho',         label: 'Meesho',         isChild: true },
];

const PARENT_CHILDREN = {
  d2c:            ['website'],
  quick_commerce: ['blinkit','swiggy','zepto','knot','slikk','zilo'],
  marketplaces:   ['amazon','myntra','flipkart','eternz','nykaa','ajio','meesho'],
};

const LEAF_CHANNELS = CHANNEL_OPTIONS.filter(c => c.isChild);

// ─── Category options ─────────────────────────────────────────────────────────

const CATEGORY_OPTIONS = [
  { value: 'all',                 label: 'All Categories' },
  { value: 'womens_earrings',     label: 'Womens Earrings' },
  { value: 'womens_bracelets',    label: 'Womens Bracelets' },
  { value: 'womens_necklaces',    label: 'Womens Necklaces' },
  { value: 'womens_rings',        label: 'Womens Rings' },
  { value: 'womens_anklets',      label: 'Womens Anklets' },
  { value: 'womens_jewellery_sets', label: 'Womens Jewellery Sets' },
  { value: 'rings',               label: 'Rings' },
  { value: 'mens_bracelets',      label: 'Mens Bracelets' },
  { value: 'mens_earrings',       label: 'Mens Earrings' },
  { value: 'mens_rings',          label: 'Mens Rings' },
  { value: 'mens_necklaces',      label: 'Mens Necklaces' },
  { value: 'mens_brooches',       label: 'Mens Brooches' },
  { value: 'mens_jewellery_set',  label: 'Mens Jewellery Set' },
  { value: 'kids_jewellery_sets', label: 'Kids Jewellery Sets' },
  { value: 'hand_accessories',    label: 'Hand Accessories' },
  { value: 'charms',              label: 'Charms' },
  { value: 'add_on',              label: 'Add-on' },
  { value: 'women_gift_boxes',    label: 'Women Gift Boxes' },
  { value: 'men_gift_boxes',      label: 'Men Gift Boxes' },
  { value: 'women_bundles',       label: 'Women Bundles' },
  { value: 'men_bundles',         label: 'Men Bundles' },
  { value: 'watches',             label: 'Watches' },
  { value: 'womens_sunglasses',   label: 'Womens Sunglasses' },
  { value: 'mens_sunglasses',     label: 'Mens Sunglasses' },
  { value: 'hair_accessories',    label: 'Hair Accessories' },
  { value: 'womens_scarves',      label: 'Womens Scarves' },
  { value: 'mens_scarves',        label: 'Mens Scarves' },
  { value: 'caps',                label: 'Caps' },
  { value: 'hats',                label: 'Hats' },
  { value: 'belts',               label: 'Belts' },
  { value: 'bags',                label: 'Bags' },
  { value: 'keychains',           label: 'Keychains' },
  { value: 'rakhi',               label: 'Rakhi' },
];

const CAT_NAME_TO_ID = {
  'Add-on':'add_on','Womens Waist Chain':'womens_waist_chain',
  'Men Gift Boxes':'men_gift_boxes','Women Gift Boxes':'women_gift_boxes',
  'Charms':'charms','Hand Accessories':'hand_accessories',
  'Kids Jewellery sets':'kids_jewellery_sets','Mens Bracelets':'mens_bracelets',
  'Mens Brooches':'mens_brooches','Mens Earrings':'mens_earrings',
  'Mens Jewellery Set':'mens_jewellery_set','Mens Necklaces':'mens_necklaces',
  'Mens Rings':'mens_rings','Rings':'rings','Women Bundles':'women_bundles',
  'Womens Anklets':'womens_anklets','Womens Bracelets':'womens_bracelets',
  'Womens Earrings':'womens_earrings','Womens Jewellery Sets':'womens_jewellery_sets',
  'Womens Necklaces':'womens_necklaces','Womens Rings':'womens_rings',
  'Keychains':'keychains','Packaging':'packaging','Womens Sunglasses':'womens_sunglasses',
  'Caps':'caps','Unknown':'unknown','Hair Accessories':'hair_accessories',
  'Hats':'hats','Rakhi':'rakhi','Womens Scarves':'womens_scarves',
  'Watches':'watches','Mens Sunglasses':'mens_sunglasses','Belts':'belts',
  'Bags':'bags','Men Bundles':'men_bundles','Women Jewellery Sets':'women_jewellery_sets',
  'Mens Scarves':'mens_scarves',
};

// ─── P&L structure ────────────────────────────────────────────────────────────

// "cost" type = collapsible block with black text — shows aggregate when collapsed,
// red individual items when expanded. Standard executive P&L presentation.
const PNL_STRUCTURE = [

  // ── Always visible: Gross Sales ───────────────────────────────────────────
  { id: 'gross_revenue_incl_gst', name: 'Gross Sales', expandable: true, hasChannelBreakdown: true, hasCategoryBreakdown: true },

  // ── Collapsed block: Discounts ────────────────────────────────────────────
  {
    id: 'discounts_block', name: 'Less : Discounts', type: 'cost', expandable: true, calculated: true,
    children: [
      { id: 'less_discounts', name: 'Discount', type: 'deduction', expandable: true, hasChannelBreakdown: true, hasCategoryBreakdown: true },
    ]
  },

  // ── Always visible: Total Sales ───────────────────────────────────────────
  { id: 'total_revenue_incl_gst', name: 'Total Sales', type: 'subtotal', calculated: true },

  // ── Collapsed block: Returns & Adjustments ────────────────────────────────
  {
    id: 'adjustments_block', name: 'Less : Returns & Adjustments', type: 'cost', expandable: true, calculated: true,
    children: [
      { id: 'less_cancellations', name: 'Cancellations', type: 'deduction', expandable: true, hasChannelBreakdown: true, hasCategoryBreakdown: true },
      { id: 'less_rto',           name: 'RTO',           type: 'deduction', expandable: true, hasChannelBreakdown: true, hasCategoryBreakdown: true },
      { id: 'less_returns',       name: 'Returns',       type: 'deduction', expandable: true, hasChannelBreakdown: true, hasCategoryBreakdown: true },
      { id: 'less_discrepancy',   name: 'Discrepancy',   type: 'deduction', expandable: true, hasChannelBreakdown: true, hasCategoryBreakdown: true },
    ]
  },

  // ── Always visible: Net Sales ─────────────────────────────────────────────
  { id: 'net_revenue', name: 'Net Sales (1)', type: 'subtotal', calculated: true, highlight: true },

  // ── Collapsed block: COGS (black parent, red children) ───────────────────
  {
    id: 'cogs', name: 'COGS', type: 'cost', expandable: true, calculated: true,
    children: [
      { id: 'cogs_product',   name: 'Material Cost', type: 'deduction', expandable: true, hasChannelBreakdown: true, hasCategoryBreakdown: true },
      { id: 'cogs_packaging', name: 'Packing',        type: 'deduction', expandable: true, hasChannelBreakdown: true, hasCategoryBreakdown: true },
    ]
  },

  // ── Always visible: Gross Margin ──────────────────────────────────────────
  { id: 'gross_margin', name: 'Gross Margin (2)', type: 'subtotal', calculated: true, highlight: true },

  // ── Collapsed block: Direct Costs (black parent, red children) ───────────
  {
    id: 'direct_costs', name: 'Direct Costs', type: 'cost', expandable: true, calculated: true,
    children: [
      { id: 'payment_gateway',   name: 'Payment Gateway',             type: 'deduction', expandable: true, hasChannelBreakdown: true, hasCategoryBreakdown: true },
      { id: 'logistics_shipping',name: 'Logistics',                   type: 'deduction', expandable: true, hasChannelBreakdown: true, hasCategoryBreakdown: true },
      { id: 'channel_margin',    name: 'Channel Margin / Commission', type: 'deduction', expandable: true, hasChannelBreakdown: true, hasCategoryBreakdown: true },
    ]
  },

  // ── Always visible: CM1 ──────────────────────────────────────────────────
  { id: 'cm1', name: 'CM1 (3)', type: 'subtotal', calculated: true, highlight: true },

  // ── Collapsed block: Performance Marketing (paid channel spend only) ─────
  {
    id: 'direct_marketing_section', name: 'Performance Marketing', type: 'cost', expandable: true, calculated: true,
    children: [
      { id: 'fb_spend', name: 'D2C / Website' },
      { id: 'marketplace_marketing', name: 'Marketplaces', type: 'cost', expandable: true, calculated: true, children: [
        { id: 'amazon_ads',   name: 'Amazon' },
        { id: 'flipkart_ads', name: 'Flipkart' },
        { id: 'myntra_ads',   name: 'Myntra' },
        { id: 'eternz_ads',   name: 'Eternz' },
        { id: 'meesho_ads',   name: 'Meesho' },
        { id: 'nykaa_ads',    name: 'Nykaa' },
        { id: 'ajio_ads',     name: 'Ajio' },
      ]},
      { id: 'qc_marketing', name: 'Quick Commerce', type: 'cost', expandable: true, calculated: true, children: [
        { id: 'blinkit_ads', name: 'Blinkit' },
        { id: 'zepto_ads',   name: 'Zepto' },
        { id: 'knot_ads',    name: 'Knot' },
        { id: 'slikk_ads',   name: 'Slikk' },
        { id: 'swiggy_ads',  name: 'Swiggy' },
        { id: 'zilo_ads',    name: 'Zilo' },
      ]},
    ]
  },

  // ── Always visible: CM2 ──────────────────────────────────────────────────
  { id: 'cm2', name: 'CM2 (4)', type: 'subtotal', calculated: true, highlight: true, primary: true },

  // ── Collapsed block: Corporate Overheads ─────────────────────────────────
  {
    id: 'corporate_overheads_section', name: 'Corporate Overheads', type: 'cost', expandable: true, calculated: true,
    children: [
      { id: 'employee_cost', name: 'Employee Cost' },
      // Brand & Marketing (Except Performance) — explicitly NOT performance marketing
      { id: 'brand_marketing', name: 'Brand & Marketing (Excl. Performance)', type: 'cost', expandable: true, calculated: true, children: [
        { id: 'content_influencer',  name: 'Content Influencer' },
        { id: 'brand_identity',      name: 'Brand Identity' },
        { id: 'branding_content',    name: 'Branding Content' },
        { id: 'seo',                 name: 'SEO' },
        { id: 'pr_agency',           name: 'PR Agency' },
        { id: 'influencer_marketing',name: 'Influencer Marketing' },
        { id: 'photography',         name: 'Photography' },
        { id: 'inhouse_shoots',      name: 'Monthly Inhouse Shoots' },
        { id: 'brand_activation',    name: 'Brand Activation' },
        { id: 'brand_film',          name: 'Brand Film' },
        { id: 'brand_other',         name: 'Other' },
      ]},
      // Tech
      { id: 'tech_expenses', name: 'Tech Overheads', type: 'cost', expandable: true, calculated: true, children: [
        { id: 'website_tools',          name: 'Website Tools' },
        { id: 'ops_tools',              name: 'Ops Tools' },
        { id: 'customer_support_tools', name: 'Customer Support Tools' },
        { id: 'website_dev',            name: 'Website Development' },
        { id: 'data_tools',             name: 'Data Tools' },
        { id: 'other_tools',            name: 'Other Tools' },
        { id: 'retention_tools',        name: 'Retention Tools' },
        { id: 'retention_spends',       name: 'Retention Spends (SMS/Email)' },
      ]},
      { id: 'staff_welfare',         name: 'Staff Welfare' },
      { id: 'professional_expenses', name: 'Professional & Agency' },
      { id: 'office_exp',            name: 'Office Expenses' },
      { id: 'warehouse_exp',         name: 'Warehouse Expenses' },
      { id: 'rental_expenses',       name: 'Rental' },
      { id: 'travel_conveyance',     name: 'Travel & Conveyance' },
      { id: 'damaged_goods',         name: 'Damaged Goods Provision' },
    ]
  },

  // ── Always visible: EBITDA ───────────────────────────────────────────────
  { id: 'ebitda', name: 'EBITDA (5)', type: 'subtotal', calculated: true, highlight: true, primary: true },
];

// Rows computed by formula — not directly editable
const CALC_IDS = new Set([
  'discounts_block', 'total_revenue_incl_gst',
  'adjustments_block', 'net_revenue',
  'cogs', 'gross_margin',
  'direct_costs', 'cm1',
  // Marketing
  'marketplace_marketing', 'qc_marketing', 'brand_marketing',
  'performance_marketing', 'direct_marketing_section',
  'cm2',
  // Overheads
  'tech_expenses',
  'total_overheads', 'corporate_overheads_section',
  'ebitda',
]);

// API key → row ID mapping
const API_TO_ROW = {
  gross_sales:     'gross_revenue_incl_gst',
  discount:        'less_discounts',
  cancellations:   'less_cancellations',
  rto:             'less_rto',
  returns:         'less_returns',
  discrepancy:     'less_discrepancy',
  material_cost:   'cogs_product',
  packing:         'cogs_packaging',
  payment_gateway: 'payment_gateway',
  logistics:       'logistics_shipping',
  channel_margins: 'channel_margin',
};
const ROW_TO_API = Object.fromEntries(Object.entries(API_TO_ROW).map(([k,v]) => [v,k]));
const NEGATIVE_KEYS = new Set(['discount','cancellations','rto','returns','discrepancy','material_cost','packing','payment_gateway','logistics','channel_margins']);

// ─── Data utilities ───────────────────────────────────────────────────────────

const round2    = v => Math.round((v || 0) * 100) / 100;
const absVal    = v => Math.abs(v || 0);
const TO_RUPEES = 100000;
const toRupees  = arr => arr.map(v => round2(v * TO_RUPEES));
const sumArrays = (...arrs) => Array((arrs[0] || []).length || 12).fill(0).map((_,i) => arrs.reduce((s,a) => s + (a?.[i] || 0), 0));

const aggregatePeriods = (monthly12, periods) => {
  if (!monthly12?.length) return Array(periods).fill(0);
  if (periods === 12) return monthly12.map(round2);
  if (periods === 4)  return [0,1,2,3].map(q => round2(monthly12.slice(q*3,q*3+3).reduce((a,b)=>a+b,0)));
  if (periods === 2)  return [round2(monthly12.slice(0,6).reduce((a,b)=>a+b,0)), round2(monthly12.slice(6).reduce((a,b)=>a+b,0))];
  if (periods === 1)  return [round2(monthly12.reduce((a,b)=>a+b,0))];
  return monthly12.map(round2);
};

// Slugify any name → consistent key (matches backend _slugify)
const slugify = (name) =>
  name.toLowerCase().trim().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '');

// Dynamic month labels for any fiscal year
// FY2027 → Apr-26 … Mar-27, FY2028 → Apr-27 … Mar-28
const getFYMonths = (fy) => {
  const sy = String((fy - 1) % 100).padStart(2, '0');
  const ey = String(fy % 100).padStart(2, '0');
  return ['Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec','Jan','Feb','Mar']
    .map((m, i) => `${m}-${i < 9 ? sy : ey}`);
};

const EMPTY_RAW = { overall:{}, byChannel:{}, byCategory:{}, byChanCat:{}, channelMeta:{}, categoryMeta:{} };

async function fetchRawData(fiscalYear = 2027) {
  const summaryRes = await aopAPI.getChannels(fiscalYear);
  const channels   = summaryRes.data;
  if (!channels?.length) return EMPTY_RAW;

  const knownChannelKeys = new Set(LEAF_CHANNELS.map(c => c.value));
  const byChannel   = {};
  const channelMeta = {};   // channel_key → { display, isKnown }

  for (const ch of channels) {
    byChannel[ch.channel] = {};
    channelMeta[ch.channel] = {
      display: ch.channel_display || ch.channel,
      isKnown: knownChannelKeys.has(ch.channel),
    };
    for (const li of ch.totals.line_items) {
      if (li.type === 'percent') continue;
      byChannel[ch.channel][li.key] = toRupees(li.monthly);
    }
  }

  const overall = {};
  for (const chData of Object.values(byChannel)) {
    for (const [key, monthly] of Object.entries(chData)) {
      overall[key] = sumArrays(overall[key] || Array(12).fill(0), monthly);
    }
  }

  const details      = await Promise.all(channels.map(ch => aopAPI.getChannelDetail(ch.channel, fiscalYear).catch(() => null)));
  const byCategory   = {};
  const byChanCat    = {};
  const categoryMeta = {};   // catId → display name

  for (const res of details) {
    if (!res?.data?.categories) continue;
    const chId = res.data.channel;
    for (const cat of res.data.categories) {
      // Use hardcoded map first; fall back to slugify so new categories are never lost
      const catId = CAT_NAME_TO_ID[cat.name] || slugify(cat.name);
      if (!catId) continue;

      categoryMeta[catId] = cat.name;   // store original display name

      const ccKey = `${chId}_${catId}`;
      byChanCat[ccKey] = {};
      for (const li of cat.line_items) {
        if (li.type === 'percent') continue;
        const monthly = toRupees(li.monthly);
        byCategory[catId] = byCategory[catId] || {};
        byCategory[catId][li.key] = sumArrays(byCategory[catId][li.key] || Array(12).fill(0), monthly);
        byChanCat[ccKey][li.key] = monthly;
      }
    }
  }
  return { overall, byChannel, byCategory, byChanCat, channelMeta, categoryMeta };
}

function getEffective12(rawData, apiKey, selectedChannel, selectedCategory) {
  const isCat  = selectedCategory !== 'all';
  const isChan = selectedChannel  !== 'all';
  if (isChan && isCat) {
    const leafIds = PARENT_CHILDREN[selectedChannel] || [selectedChannel];
    return sumArrays(...leafIds.map(ch => rawData.byChanCat[`${ch}_${selectedCategory}`]?.[apiKey] || Array(12).fill(0)));
  }
  if (isChan) {
    const leafIds = PARENT_CHILDREN[selectedChannel] || [selectedChannel];
    return sumArrays(...leafIds.map(ch => rawData.byChannel[ch]?.[apiKey] || Array(12).fill(0)));
  }
  if (isCat) return rawData.byCategory[selectedCategory]?.[apiKey] || Array(12).fill(0);
  return rawData.overall[apiKey] || Array(12).fill(0);
}

// ─── Main component ───────────────────────────────────────────────────────────

export default function AOPPage() {
  const [loading,         setLoading]          = useState(true);
  const [saving,          setSaving]           = useState(false);
  const [rawData,         setRawData]          = useState(EMPTY_RAW);
  const [editableData,    setEditableData]     = useState({});
  const [timePeriod,      setTimePeriod]       = useState('monthly');
  const [selectedChannel, setSelectedChannel]  = useState('all');
  const [selectedCategory,setSelectedCategory] = useState('all');
  const [expandedRows,    setExpandedRows]     = useState(new Set());
  const [aopId,           setAopId]            = useState(null);
  const [planDetails,     setPlanDetails]      = useState({ name: 'FY 2026-27 Operating Plan', fiscal_year: 2027, scenario: 'base' });

  // Dialogs
  const [showVersionHistory,  setShowVersionHistory]  = useState(false);
  const [showSaveVersion,     setShowSaveVersion]     = useState(false);
  const [versions,            setVersions]            = useState([]);
  const [selectedVersions,    setSelectedVersions]    = useState([]);
  const [versionDescription,  setVersionDescription]  = useState('');
  const [loadingVersions,     setLoadingVersions]     = useState(false);
  const [showImportDialog,    setShowImportDialog]    = useState(false);
  const [importFile,          setImportFile]          = useState(null);
  const [importing,           setImporting]           = useState(false);
  const [editMode,            setEditMode]            = useState(false);
  const [lastSaved,           setLastSaved]           = useState(null);
  const [dataVersion,         setDataVersion]         = useState(0);
  const [selectedFiscalYear,  setSelectedFiscalYear]  = useState(2027);
  const [availableFYs,        setAvailableFYs]        = useState([2027]);
  const autoSaveTimer                                 = useRef(null);

  const { format: numFmt }    = useNumberFormat();
  const fmt = useCallback(v => applyFormat(v, numFmt), [numFmt]);
  const { track } = useClickstream();

  // Dynamic period labels — recalculate when fiscal year changes
  const fyMonths  = useMemo(() => getFYMonths(selectedFiscalYear), [selectedFiscalYear]);
  const fyShort   = String(selectedFiscalYear % 100).padStart(2, '0');
  const periodConfig = useMemo(() => ({
    ...TIME_PERIODS[timePeriod],
    format: (i) => {
      if (timePeriod === 'monthly')     return fyMonths[i];
      if (timePeriod === 'quarterly')   return `Q${i+1} FY${fyShort}`;
      if (timePeriod === 'half_yearly') return i === 0 ? `H1 FY${fyShort}` : `H2 FY${fyShort}`;
      return `FY${selectedFiscalYear}`;
    },
  }), [timePeriod, fyMonths, fyShort, selectedFiscalYear]);

  // ── Dynamic channel & category lists (extend hardcoded with any new ones from DB) ──

  const dynamicChannelOptions = useMemo(() => {
    const knownKeys  = new Set(LEAF_CHANNELS.map(c => c.value));
    const newChannels = Object.keys(rawData.channelMeta || {})
      .filter(k => !knownKeys.has(k))
      .map(k => ({ value: k, label: rawData.channelMeta[k]?.display || k, isChild: true }));

    if (!newChannels.length) return CHANNEL_OPTIONS;

    return [
      ...CHANNEL_OPTIONS,
      { value: '_new_header', label: 'New Channels', isParent: true },
      ...newChannels,
    ];
  }, [rawData.channelMeta]);

  const dynamicCategoryOptions = useMemo(() => {
    const knownIds  = new Set(CATEGORY_OPTIONS.filter(c => c.value !== 'all').map(c => c.value));
    const newCats = Object.entries(rawData.categoryMeta || {})
      .filter(([id]) => !knownIds.has(id))
      .map(([id, name]) => ({ value: id, label: name }));

    if (!newCats.length) return CATEGORY_OPTIONS;
    return [...CATEGORY_OPTIONS, ...newCats];
  }, [rawData.categoryMeta]);

  // Leaf channels for breakdown rows — includes any new channels
  const dynamicLeafChannels = useMemo(() => {
    const knownKeys = new Set(LEAF_CHANNELS.map(c => c.value));
    const newLeafs  = Object.entries(rawData.channelMeta || {})
      .filter(([k]) => !knownKeys.has(k))
      .map(([k, meta]) => ({ value: k, label: meta?.display || k, isChild: true }));
    return [...LEAF_CHANNELS, ...newLeafs];
  }, [rawData.channelMeta]);

  // ── Load raw data once ────────────────────────────────────────────────────────

  // Load data, restore aopId, and fetch available fiscal years
  useEffect(() => {
    setLoading(true);
    Promise.all([
      fetchRawData(selectedFiscalYear),
      aopAPI.getLatestPlan(selectedFiscalYear).catch(() => null),
      aopAPI.getFiscalYears().catch(() => null),
      aopAPI.getOverhead(selectedFiscalYear).catch(() => null),
    ]).then(([data, planRes, fyRes, overheadRes]) => {
      setRawData(data);
      if (planRes?.data?.id) setAopId(planRes.data.id);
      if (fyRes?.data?.length) {
        const years = Array.from(new Set([...fyRes.data, selectedFiscalYear])).sort();
        setAvailableFYs(years);
      }
      // Pre-populate overhead rows from the ingested overhead_aop data
      if (overheadRes?.data?.data) {
        const ohData = overheadRes.data.data;
        const periods = TIME_PERIODS['monthly'].periods; // always 12 for overhead
        setEditableData(prev => {
          const next = { ...prev };
          for (const [key, monthly12] of Object.entries(ohData)) {
            // Convert Lakhs → Rupees, pad/trim to 12
            const vals = Array(12).fill(0).map((_, i) => round2((monthly12[i] || 0) * 100000));
            next[key] = vals;
          }
          return next;
        });
      }
    }).catch(err => {
      console.error(err);
      toast.error('Failed to load AOP data');
    }).finally(() => setLoading(false));
  }, [selectedFiscalYear, dataVersion]); // eslint-disable-line

  // When FY changes, reset aopId (new year = new plan)
  useEffect(() => {
    setAopId(null);
    setLastSaved(null);
  }, [selectedFiscalYear]);

  // ── Sync editableData when rawData / filter / timePeriod changes ─────────────

  useEffect(() => {
    if (!rawData.overall || Object.keys(rawData.overall).length === 0) return;
    const periods = periodConfig.periods;
    const agg = (apiKey, negate = false) => {
      const monthly12 = getEffective12(rawData, apiKey, selectedChannel, selectedCategory);
      const vals = negate ? monthly12.map(absVal) : monthly12;
      return aggregatePeriods(vals, periods);
    };

    setEditableData(prev => ({
      // API-sourced rows — refreshed on filter change
      gross_revenue_incl_gst: agg('gross_sales'),
      less_discounts:         agg('discount',        true),
      // Use Excel's formula-computed Total Sales to avoid cross-category rounding drift
      total_sales_stored:     agg('total_sales'),
      less_cancellations:     agg('cancellations',   true),
      less_rto:               agg('rto',             true),
      less_returns:           agg('returns',         true),
      less_discrepancy:       agg('discrepancy',     true),
      cogs_product:           agg('material_cost',   true),
      cogs_packaging:         agg('packing',         true),
      payment_gateway:        agg('payment_gateway', true),
      logistics_shipping:     agg('logistics',       true),
      channel_margin:         agg('channel_margins', true),

      // Channel breakdown keys: rowId__ch__channelId
      ...Object.fromEntries(
        dynamicLeafChannels.flatMap(ch =>
          Object.entries(ROW_TO_API).map(([rowId, apiKey]) => {
            const m12 = rawData.byChannel[ch.value]?.[apiKey] || Array(12).fill(0);
            const vals = NEGATIVE_KEYS.has(apiKey) ? m12.map(absVal) : m12;
            return [`${rowId}__ch__${ch.value}`, aggregatePeriods(vals, periods)];
          })
        )
      ),

      // Category breakdown keys: rowId__cat__catId
      ...Object.fromEntries(
        dynamicCategoryOptions.filter(c => c.value !== 'all').flatMap(cat =>
          Object.entries(ROW_TO_API).map(([rowId, apiKey]) => {
            const m12 = (selectedChannel !== 'all'
              ? sumArrays(...(PARENT_CHILDREN[selectedChannel] || [selectedChannel]).map(ch => rawData.byChanCat[`${ch}_${cat.value}`]?.[apiKey] || Array(12).fill(0)))
              : rawData.byCategory[cat.value]?.[apiKey] || Array(12).fill(0));
            const vals = NEGATIVE_KEYS.has(apiKey) ? m12.map(absVal) : m12;
            return [`${rowId}__cat__${cat.value}`, aggregatePeriods(vals, periods)];
          })
        )
      ),

      // ── Performance Marketing (exactly from sheet) ───────────────────────
      // D2C
      fb_spend:               prev.fb_spend               || Array(periods).fill(0),
      // Marketplace
      amazon_ads:             prev.amazon_ads             || Array(periods).fill(0),
      flipkart_ads:           prev.flipkart_ads           || Array(periods).fill(0),
      myntra_ads:             prev.myntra_ads             || Array(periods).fill(0),
      eternz_ads:             prev.eternz_ads             || Array(periods).fill(0),
      meesho_ads:             prev.meesho_ads             || Array(periods).fill(0),
      nykaa_ads:              prev.nykaa_ads              || Array(periods).fill(0),
      ajio_ads:               prev.ajio_ads               || Array(periods).fill(0),
      // Quick Commerce
      blinkit_ads:            prev.blinkit_ads            || Array(periods).fill(0),
      zepto_ads:              prev.zepto_ads              || Array(periods).fill(0),
      knot_ads:               prev.knot_ads               || Array(periods).fill(0),
      slikk_ads:              prev.slikk_ads              || Array(periods).fill(0),
      swiggy_ads:             prev.swiggy_ads             || Array(periods).fill(0),
      zilo_ads:               prev.zilo_ads               || Array(periods).fill(0),
      // ── Brand & Marketing (exactly from sheet) ───────────────────────────
      content_influencer:     prev.content_influencer     || Array(periods).fill(0),
      brand_identity:         prev.brand_identity         || Array(periods).fill(0),
      branding_content:       prev.branding_content       || Array(periods).fill(0),
      seo:                    prev.seo                    || Array(periods).fill(0),
      pr_agency:              prev.pr_agency              || Array(periods).fill(0),
      influencer_marketing:   prev.influencer_marketing   || Array(periods).fill(0),
      photography:            prev.photography            || Array(periods).fill(0),
      inhouse_shoots:         prev.inhouse_shoots         || Array(periods).fill(0),
      brand_activation:       prev.brand_activation       || Array(periods).fill(0),
      brand_film:             prev.brand_film             || Array(periods).fill(0),
      brand_other:            prev.brand_other            || Array(periods).fill(0),
      // ── Overheads (exactly from sheet) ───────────────────────────────────
      employee_cost:          prev.employee_cost          || Array(periods).fill(0),
      // Tech Overheads
      website_tools:          prev.website_tools          || Array(periods).fill(0),
      ops_tools:              prev.ops_tools              || Array(periods).fill(0),
      customer_support_tools: prev.customer_support_tools || Array(periods).fill(0),
      website_dev:            prev.website_dev            || Array(periods).fill(0),
      data_tools:             prev.data_tools             || Array(periods).fill(0),
      other_tools:            prev.other_tools            || Array(periods).fill(0),
      retention_tools:        prev.retention_tools        || Array(periods).fill(0),
      retention_spends:       prev.retention_spends       || Array(periods).fill(0),
      // Other overheads
      staff_welfare:          prev.staff_welfare          || Array(periods).fill(0),
      professional_expenses:  prev.professional_expenses  || Array(periods).fill(0),
      office_exp:             prev.office_exp             || Array(periods).fill(0),
      warehouse_exp:          prev.warehouse_exp          || Array(periods).fill(0),
      rental_expenses:        prev.rental_expenses        || Array(periods).fill(0),
      travel_conveyance:      prev.travel_conveyance      || Array(periods).fill(0),
      damaged_goods:          prev.damaged_goods          || Array(periods).fill(0),
    }));
  }, [rawData, selectedChannel, selectedCategory, timePeriod, selectedFiscalYear, periodConfig.periods]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Computed P&L (formula fields auto-calculate from editableData) ────────────

  const computedPL = useMemo(() => {
    const periods = periodConfig.periods;
    const ed = id => editableData[id] || Array(periods).fill(0);

    const gs    = ed('gross_revenue_incl_gst');
    const disc  = ed('less_discounts');
    const canc  = ed('less_cancellations');
    const rto   = ed('less_rto');
    const ret   = ed('less_returns');
    const dsc2  = ed('less_discrepancy');
    const matc  = ed('cogs_product');
    const pack  = ed('cogs_packaging');
    const pg    = ed('payment_gateway');
    const log   = ed('logistics_shipping');
    const chan  = ed('channel_margin');

    // ── Formula calculations (cascade) ──────────────────────────────────────
    // Revenue waterfall — use Excel's formula-computed Total Sales as anchor
    // to avoid cross-category rounding drift (~4.88L over all channels)
    const discountsBlock    = disc;
    const totalRev          = ed('total_sales_stored').map((v, i) => v || round2(gs[i] - disc[i]));
    const adjustmentsBlock  = canc.map((v, i) => round2(v + rto[i] + ret[i] + dsc2[i]));
    const netRev            = totalRev.map((v, i) => round2(v - adjustmentsBlock[i]));

    // Gross Margin = Net Sales − COGS (Material + Packing) only
    const cogs = matc.map((v, i) => round2(v + pack[i]));
    const gm   = netRev.map((v, i) => round2(v - cogs[i]));

    // Direct costs subtotal
    const directCosts = pg.map((v, i) => round2(v + log[i] + chan[i]));
    // CM1 = Gross Margin − Direct Costs
    const cm1  = gm.map((v, i) => round2(v - directCosts[i]));

    // Performance Marketing = D2C + Marketplace channels + QC channels (paid spend only)
    const mktAds  = ed('amazon_ads').map((v,i) => round2(v + ed('flipkart_ads')[i] + ed('myntra_ads')[i] + ed('eternz_ads')[i] + ed('meesho_ads')[i] + ed('nykaa_ads')[i] + ed('ajio_ads')[i]));
    const qcAds   = ed('blinkit_ads').map((v,i) => round2(v + ed('zepto_ads')[i] + ed('knot_ads')[i] + ed('slikk_ads')[i] + ed('swiggy_ads')[i] + ed('zilo_ads')[i]));
    const perfMkt = ed('fb_spend').map((v,i) => round2(v + mktAds[i] + qcAds[i]));
    const cm2     = cm1.map((v,i) => round2(v - perfMkt[i]));

    // Brand & Marketing (Except Performance) — part of Corporate Overheads
    const brandMkt = ed('content_influencer').map((v,i) => round2(
      v + ed('brand_identity')[i] + ed('branding_content')[i] + ed('seo')[i] + ed('pr_agency')[i] +
      ed('influencer_marketing')[i] + ed('photography')[i] + ed('inhouse_shoots')[i] +
      ed('brand_activation')[i] + ed('brand_film')[i] + ed('brand_other')[i]
    ));
    // Tech Overheads subtotal
    const tech = ed('website_tools').map((v,i) => round2(
      v + ed('ops_tools')[i] + ed('customer_support_tools')[i] + ed('website_dev')[i] +
      ed('data_tools')[i] + ed('other_tools')[i] + ed('retention_tools')[i] + ed('retention_spends')[i]
    ));
    // Total Corporate Overheads = Employee Cost + Brand & Marketing + Tech + all other overheads
    const oh = ed('employee_cost').map((v,i) => round2(
      v + brandMkt[i] + tech[i] +
      ed('staff_welfare')[i] + ed('professional_expenses')[i] +
      ed('office_exp')[i] + ed('warehouse_exp')[i] + ed('rental_expenses')[i] +
      ed('travel_conveyance')[i] + ed('damaged_goods')[i]
    ));
    const ebit = cm2.map((v,i) => round2(v - oh[i]));

    return {
      // Revenue input rows
      gross_revenue_incl_gst: gs,
      less_discounts: disc, discounts_block: discountsBlock,
      less_cancellations: canc, less_rto: rto, less_returns: ret, less_discrepancy: dsc2,
      adjustments_block: adjustmentsBlock,
      // Cost input rows
      cogs_product: matc, cogs_packaging: pack,
      payment_gateway: pg, logistics_shipping: log, channel_margin: chan,
      // Marketing input rows
      fb_spend: ed('fb_spend'), amazon_ads: ed('amazon_ads'), flipkart_ads: ed('flipkart_ads'),
      myntra_ads: ed('myntra_ads'), eternz_ads: ed('eternz_ads'), meesho_ads: ed('meesho_ads'),
      nykaa_ads: ed('nykaa_ads'), ajio_ads: ed('ajio_ads'),
      blinkit_ads: ed('blinkit_ads'), zepto_ads: ed('zepto_ads'), knot_ads: ed('knot_ads'),
      slikk_ads: ed('slikk_ads'), swiggy_ads: ed('swiggy_ads'), zilo_ads: ed('zilo_ads'),
      content_influencer: ed('content_influencer'), influencer_marketing: ed('influencer_marketing'),
      brand_activation: ed('brand_activation'), brand_identity: ed('brand_identity'),
      branding_content: ed('branding_content'), seo: ed('seo'), pr_agency: ed('pr_agency'),
      photography: ed('photography'), inhouse_shoots: ed('inhouse_shoots'),
      brand_film: ed('brand_film'), brand_other: ed('brand_other'),
      // Overhead input rows
      employee_cost: ed('employee_cost'),
      website_tools: ed('website_tools'), ops_tools: ed('ops_tools'),
      customer_support_tools: ed('customer_support_tools'), website_dev: ed('website_dev'),
      data_tools: ed('data_tools'), other_tools: ed('other_tools'),
      retention_tools: ed('retention_tools'), retention_spends: ed('retention_spends'),
      staff_welfare: ed('staff_welfare'), professional_expenses: ed('professional_expenses'),
      office_exp: ed('office_exp'), warehouse_exp: ed('warehouse_exp'),
      rental_expenses: ed('rental_expenses'), travel_conveyance: ed('travel_conveyance'),
      damaged_goods: ed('damaged_goods'),
      // Calculated subtotals
      total_revenue_incl_gst: totalRev, net_revenue: netRev,
      cogs, gross_margin: gm, direct_costs: directCosts, cm1,
      marketplace_marketing: mktAds, qc_marketing: qcAds,
      performance_marketing: perfMkt,
      direct_marketing_section: perfMkt,   // Performance Marketing only (no brand)
      brand_marketing: brandMkt,            // In overheads
      cm2,
      tech_expenses: tech, total_overheads: oh, corporate_overheads_section: oh, ebitda: ebit,
    };
  }, [editableData, periodConfig.periods]);

  // ── Helpers ───────────────────────────────────────────────────────────────────

  const getRowValue = useCallback((id, i) => computedPL[id]?.[i] ?? editableData[id]?.[i] ?? 0, [computedPL, editableData]);
  const getRowTotal = useCallback((id) => {
    const arr = computedPL[id] ?? editableData[id] ?? [];
    return arr.reduce((s, v) => s + v, 0);
  }, [computedPL, editableData]);

  const updateEditable = useCallback((id, periodIndex, value) => {
    const oldVal = editableData[id]?.[periodIndex] ?? 0;
    const newVal = parseFloat(value) || 0;
    setEditableData(prev => {
      const arr = [...(prev[id] || Array(periodConfig.periods).fill(0))];
      arr[periodIndex] = newVal;
      return { ...prev, [id]: arr };
    });
    track(EVENTS.AOP_CELL_EDIT, {
      row_id:         id,
      period_index:   periodIndex,
      period_label:   periodConfig.format(periodIndex),
      old_value:      oldVal,
      new_value:      newVal,
      fiscal_year:    selectedFiscalYear,
      channel_filter: selectedChannel,
      category_filter:selectedCategory,
    });
  }, [editableData, periodConfig, selectedFiscalYear, selectedChannel, selectedCategory, track]);

  // Channel breakdown — reads from editableData (pre-populated from API, editable by user)
  const getChannelBreakdown = useCallback((rowId, channelId) => {
    const edKey = `${rowId}__ch__${channelId}`;
    if (editableData[edKey]) return editableData[edKey];
    // Fallback to API for leaf channels
    const apiKey = ROW_TO_API[rowId];
    if (!apiKey) return Array(periodConfig.periods).fill(0);
    const leafIds = PARENT_CHILDREN[channelId] || [channelId];
    const isCat = selectedCategory !== 'all';
    const m12 = sumArrays(...leafIds.map(ch =>
      isCat ? rawData.byChanCat[`${ch}_${selectedCategory}`]?.[apiKey] || Array(12).fill(0)
            : rawData.byChannel[ch]?.[apiKey] || Array(12).fill(0)
    ));
    return aggregatePeriods(NEGATIVE_KEYS.has(apiKey) ? m12.map(absVal) : m12, periodConfig.periods);
  }, [editableData, rawData, selectedCategory, periodConfig.periods]);

  // Category breakdown — reads from editableData
  const getCategoryBreakdown = useCallback((rowId, catId) => {
    const edKey = `${rowId}__cat__${catId}`;
    if (editableData[edKey]) return editableData[edKey];
    const apiKey = ROW_TO_API[rowId];
    if (!apiKey) return Array(periodConfig.periods).fill(0);
    const m12 = selectedChannel !== 'all'
      ? sumArrays(...(PARENT_CHILDREN[selectedChannel] || [selectedChannel]).map(ch => rawData.byChanCat[`${ch}_${catId}`]?.[apiKey] || Array(12).fill(0)))
      : rawData.byCategory[catId]?.[apiKey] || Array(12).fill(0);
    return aggregatePeriods(NEGATIVE_KEYS.has(apiKey) ? m12.map(absVal) : m12, periodConfig.periods);
  }, [editableData, rawData, selectedChannel, periodConfig.periods]);

  // Edit a channel sub-row value
  const updateChannelValue = useCallback((rowId, channelId, periodIndex, value) => {
    setEditableData(prev => {
      const edKey = `${rowId}__ch__${channelId}`;
      const arr   = [...(prev[edKey] || Array(periodConfig.periods).fill(0))];
      arr[periodIndex] = parseFloat(value) || 0;
      const next  = { ...prev, [edKey]: arr };
      // Recompute parent = sum of all leaf channel values for this row
      const parent = Array(periodConfig.periods).fill(0);
      for (const ch of dynamicLeafChannels) {
        const chVals = next[`${rowId}__ch__${ch.value}`] || Array(periodConfig.periods).fill(0);
        for (let i = 0; i < periodConfig.periods; i++) parent[i] = round2(parent[i] + (chVals[i] || 0));
      }
      next[rowId] = parent;
      return next;
    });
    track(EVENTS.AOP_CHANNEL_EDIT, {
      row_id: rowId, channel_id: channelId,
      period_index: periodIndex, period_label: periodConfig.format(periodIndex),
      new_value: parseFloat(value) || 0, fiscal_year: selectedFiscalYear,
    });
  }, [periodConfig, selectedFiscalYear, track, dynamicLeafChannels]);

  const updateCategoryValue = useCallback((rowId, catId, periodIndex, value) => {
    setEditableData(prev => {
      const edKey = `${rowId}__cat__${catId}`;
      const arr   = [...(prev[edKey] || Array(periodConfig.periods).fill(0))];
      arr[periodIndex] = parseFloat(value) || 0;
      return { ...prev, [edKey]: arr };
    });
    track(EVENTS.AOP_CATEGORY_EDIT, {
      row_id: rowId, category_id: catId,
      period_index: periodIndex, period_label: periodConfig.format(periodIndex),
      new_value: parseFloat(value) || 0, fiscal_year: selectedFiscalYear,
    });
  }, [periodConfig, selectedFiscalYear, track]);

  const toggleExpand = useCallback(id => {
    setExpandedRows(prev => {
      const n = new Set(prev);
      const expanding = !n.has(id);
      expanding ? n.add(id) : n.delete(id);
      track(expanding ? EVENTS.AOP_ROW_EXPAND : EVENTS.AOP_ROW_COLLAPSE, { row_id: id });
      return n;
    });
  }, [track]);
  const expandAll = useCallback(() => {
    const all = []; const collect = items => items.forEach(item => { if (item.expandable) all.push(item.id); if (item.children) collect(item.children); }); collect(PNL_STRUCTURE); setExpandedRows(new Set(all));
    track(EVENTS.AOP_EXPAND_ALL, { fiscal_year: selectedFiscalYear });
  }, [track, selectedFiscalYear]);
  const collapseAll = useCallback(() => {
    setExpandedRows(new Set());
    track(EVENTS.AOP_COLLAPSE_ALL, { fiscal_year: selectedFiscalYear });
  }, [track, selectedFiscalYear]);

  // ── Breakdown sub-rows ────────────────────────────────────────────────────────

  const renderBreakdownRows = (item, depth, type) => {
    const items = type === 'channel'
      ? dynamicLeafChannels.filter(c => selectedChannel === 'all' || c.value === selectedChannel || (PARENT_CHILDREN[selectedChannel] || []).includes(c.value))
      : dynamicCategoryOptions.filter(c => c.value !== 'all' && (selectedCategory === 'all' || c.value === selectedCategory));

    return items.map(bi => {
      const vals  = type === 'channel' ? getChannelBreakdown(item.id, bi.value) : getCategoryBreakdown(item.id, bi.value);
      const total = vals.reduce((s, v) => s + v, 0);
      const isApiRow = !!ROW_TO_API[item.id];
      return (
        <tr key={`${item.id}-${bi.value}`} className="hover:bg-slate-50/50">
          <td className="px-3 py-1.5 border-b border-slate-50 border-r border-r-slate-200 sticky left-0 z-10 bg-white shadow-[2px_0_4px_-2px_rgba(0,0,0,0.06)] text-slate-500 text-xs" style={{ paddingLeft: `${(depth+1)*16+12}px` }}>
            {bi.label || bi.name}
          </td>
          {Array(periodConfig.periods).fill(0).map((_,i) => (
            <td key={i} className="px-1 py-1 border-b border-slate-50 text-right">
              {editMode && isApiRow ? (
                <Input
                  type="number"
                  value={vals[i] ?? ''}
                  onChange={e => type === 'channel'
                    ? updateChannelValue(item.id, bi.value, i, e.target.value)
                    : updateCategoryValue(item.id, bi.value, i, e.target.value)
                  }
                  className="w-full h-6 text-right font-mono text-xs px-1 py-0 border-0 bg-transparent hover:bg-white focus:bg-white focus:ring-1 focus:ring-primary-500"
                />
              ) : (
                <span className="font-mono text-xs text-slate-600 px-1">{fmt(vals[i])}</span>
              )}
            </td>
          ))}
          <td className="px-2 py-1.5 border-b border-slate-50 text-right font-mono text-xs font-semibold text-slate-700 bg-slate-50 border-l border-slate-200">{fmt(total)}</td>
        </tr>
      );
    });
  };

  // ── P&L row ───────────────────────────────────────────────────────────────────

  const renderRow = (item, depth = 0) => {
    const isExpanded   = expandedRows.has(item.id);
    const hasChildren  = item.children?.length > 0;
    const isCalc       = CALC_IDS.has(item.id);
    const isHeader     = item.type === 'header';
    const isSubtotal   = item.type === 'subtotal' || item.calculated;
    const isHighlight  = item.highlight;
    const isPrimary    = item.primary;
    const isDeduction  = item.type === 'deduction';
    const isCost       = item.type === 'cost';       // collapsible aggregate — black, not red
    const isEditable   = editMode && !isCalc && !isHeader && !isSubtotal && !hasChildren && !isCost;
    const hasBreakdown = item.hasChannelBreakdown || item.hasCategoryBreakdown;

    const bg = cn(
      isHeader    && 'bg-slate-100',
      isHighlight && !isPrimary && 'bg-emerald-50/60',
      isPrimary   && 'bg-primary-100',
      isSubtotal  && !isHighlight && !isPrimary && 'bg-slate-50',
      isCost      && 'bg-white',
      !isHeader && !isSubtotal && !isCost && 'bg-white'
    );

    return (
      <React.Fragment key={item.id}>
        <tr className={cn('group transition-colors', bg)}>
          <td
            className={cn(
              'px-3 py-2 border-b border-slate-100 border-r border-r-slate-200 sticky left-0 z-10 shadow-[2px_0_4px_-2px_rgba(0,0,0,0.08)]',
              // Solid (non-transparent) backgrounds so scrolled content doesn't bleed through
              isHeader    ? 'bg-slate-100' :
              isPrimary   ? 'bg-primary-100' :
              isHighlight ? 'bg-emerald-50' :
              isSubtotal  ? 'bg-slate-50'  : 'bg-white'
            )}
            style={{ paddingLeft: `${depth*16+12}px` }}
          >
            <div className="flex items-center gap-1.5">
              {(hasChildren || hasBreakdown)
                ? <button onClick={() => toggleExpand(item.id)} className="p-0.5 hover:bg-slate-200 rounded flex-shrink-0">
                    {isExpanded ? <ChevronDown className="w-4 h-4 text-slate-400" /> : <ChevronRight className="w-4 h-4 text-slate-400" />}
                  </button>
                : <span className="w-5" />
              }
              <span className={cn(
                'text-sm',
                isDeduction && !isCost && 'text-red-600',
                isCost      && 'text-slate-700 font-medium',
                isHeader    && 'font-semibold text-slate-700',
                (isSubtotal || isPrimary) && 'font-semibold'
              )}>
                {item.name}
              </span>
            </div>
          </td>

          {Array(periodConfig.periods).fill(0).map((_,i) => (
            <td key={i} className={cn('px-1 py-1.5 border-b border-slate-100 text-right', isSubtotal && 'font-semibold', isPrimary && 'text-primary-900')}>
              {isHeader ? null : isEditable ? (
                <Input
                  type="number"
                  value={editableData[item.id]?.[i] ?? ''}
                  onChange={e => updateEditable(item.id, i, e.target.value)}
                  className="w-full h-7 text-right font-mono text-sm px-1 py-0 border-0 bg-transparent hover:bg-white focus:bg-white focus:ring-1 focus:ring-primary-500"
                />
              ) : (
                <span className={cn('font-mono text-sm px-1', getRowValue(item.id, i) < 0 && 'text-red-600')}>
                  {fmt(getRowValue(item.id, i))}
                </span>
              )}
            </td>
          ))}

          <td className={cn('px-2 py-2 border-b border-slate-100 text-right font-mono text-sm font-semibold border-l border-slate-200', isSubtotal ? 'bg-slate-100' : 'bg-slate-50', isHighlight && 'bg-emerald-100/50', isPrimary && 'bg-primary-100')}>
            {!isHeader && <span className={cn(getRowTotal(item.id) < 0 && 'text-red-600')}>{fmt(getRowTotal(item.id))}</span>}
          </td>
        </tr>

        {isExpanded && item.hasChannelBreakdown && (<>
          <tr className="bg-blue-50/30">
            <td className="px-3 py-1 border-b border-slate-100 border-r border-r-slate-200 sticky left-0 bg-blue-50 z-10 text-xs font-medium text-blue-700 shadow-[2px_0_4px_-2px_rgba(0,0,0,0.06)]" style={{ paddingLeft: `${(depth+1)*16+12}px` }} colSpan={periodConfig.periods+2}>
              <div className="flex items-center gap-1"><Layers className="w-3 h-3" /> Channel Breakdown</div>
            </td>
          </tr>
          {renderBreakdownRows(item, depth, 'channel')}
        </>)}

        {isExpanded && item.hasCategoryBreakdown && (<>
          <tr className="bg-purple-50/30">
            <td className="px-3 py-1 border-b border-slate-100 border-r border-r-slate-200 sticky left-0 bg-purple-50 z-10 text-xs font-medium text-purple-700 shadow-[2px_0_4px_-2px_rgba(0,0,0,0.06)]" style={{ paddingLeft: `${(depth+1)*16+12}px` }} colSpan={periodConfig.periods+2}>
              <div className="flex items-center gap-1"><BarChart3 className="w-3 h-3" /> Category Breakdown</div>
            </td>
          </tr>
          {renderBreakdownRows(item, depth, 'category')}
        </>)}

        {isExpanded && hasChildren && item.children.map(child => renderRow(child, depth+1))}
      </React.Fragment>
    );
  };

  // ── Version control & Excel ───────────────────────────────────────────────────

  // ── Save / auto-save ──────────────────────────────────────────────────────────

  const buildPayload = useCallback(() => ({
    name:          `FY${selectedFiscalYear} Operating Plan`,
    fiscal_year:   selectedFiscalYear,
    scenario:      'base',
    planning_data: editableData,
  }), [selectedFiscalYear, editableData]);

  const handleSave = useCallback(async (silent = false) => {
    setSaving(true);
    try {
      let id = aopId;

      // If aopId is null (e.g. hard reset), try DB first before creating anything.
      // The backend POST is now an atomic upsert so even if we call it with null
      // aopId it will find-or-create the correct plan and never duplicate it.
      if (!id) {
        try {
          const existing = await aopAPI.getLatestPlan(selectedFiscalYear);
          id = existing.data.id;
          setAopId(id);
        } catch {
          // No plan yet — fall through to savePlan below
        }
      }

      if (id) {
        await aopAPI.updatePlan(id, buildPayload());
      } else {
        // POST is an atomic upsert — safe to call even under race conditions
        const res = await aopAPI.savePlan(buildPayload());
        id = res.data.id;
        setAopId(id);
      }

      setLastSaved(new Date());
      if (!silent) {
        toast.success('AOP plan saved');
        track(EVENTS.AOP_PLAN_SAVE, { plan_id: id, fiscal_year: selectedFiscalYear });
      }
      return id;
    } catch (e) {
      if (!silent) toast.error('Save failed: ' + (e.response?.data?.detail || e.message));
      return null;
    } finally {
      setSaving(false);
    }
  }, [aopId, selectedFiscalYear, buildPayload, track]);

  // Auto-save 30 s after last edit (only when editMode is on)
  useEffect(() => {
    if (!editMode || Object.keys(editableData).length === 0) return;
    clearTimeout(autoSaveTimer.current);
    autoSaveTimer.current = setTimeout(() => handleSave(true), 30000);
    return () => clearTimeout(autoSaveTimer.current);
  }, [editableData, editMode, handleSave]);

  // ── Version control ────────────────────────────────────────────────────────────

  // Resolve the plan ID — prefer DB lookup over creating a new plan,
  // so version history is never orphaned after a hard reset.
  const resolvePlanId = useCallback(async () => {
    if (aopId) return aopId;

    // Try to find an existing plan in the DB for this user + fiscal year
    try {
      const res = await aopAPI.getLatestPlan(selectedFiscalYear);
      const id  = res.data.id;
      setAopId(id);   // restore for future use
      return id;
    } catch {
      // No saved plan yet — create one now
      const id = await handleSave(true);
      return id;
    }
  }, [aopId, selectedFiscalYear, handleSave]);

  const loadVersions = async () => {
    const id = await resolvePlanId();
    if (!id) { toast.info('Save the plan first to access version history'); return; }

    setLoadingVersions(true);
    try {
      const r = await aopAPI.getPlanVersions(id);
      setVersions(r.data);
      setShowVersionHistory(true);
    } catch { toast.error('Failed to load versions'); }
    finally { setLoadingVersions(false); }
  };

  const saveVersion = async () => {
    const id = await resolvePlanId();
    if (!id) { toast.error('Could not resolve plan — try saving first'); return; }

    try {
      await aopAPI.savePlanVersion(id, versionDescription);
      toast.success('Version saved successfully');
      track(EVENTS.AOP_VERSION_SAVE, { plan_id: id, description: versionDescription, fiscal_year: selectedFiscalYear });
      setShowSaveVersion(false);
      setVersionDescription('');
    } catch (e) {
      toast.error('Failed to save version: ' + (e.response?.data?.detail || e.message));
    }
  };

  const restoreVersion = async (version) => {
    try {
      const snapshot = version.snapshot?.planning_data;
      if (!snapshot) { toast.error('No data found in this snapshot'); return; }

      // 1. Load snapshot into UI immediately
      setEditableData(snapshot);

      // 2. Persist the restored state to the database so it survives a refresh
      const restoredPayload = {
        name:          `Restored from v${version.version_number}`,
        fiscal_year:   selectedFiscalYear,
        scenario:      'base',
        planning_data: snapshot,
      };

      const id = await resolvePlanId();
      if (id) {
        await aopAPI.updatePlan(id, restoredPayload);
      } else {
        const res = await aopAPI.savePlan(restoredPayload);
        setAopId(res.data.id);
      }

      setLastSaved(new Date());
      track(EVENTS.AOP_VERSION_RESTORE, { plan_id: aopId, version_number: version.version_number, description: version.description, fiscal_year: selectedFiscalYear });
      toast.success(`Restored to v${version.version_number}${version.description ? ` — "${version.description}"` : ''}`);
      setShowVersionHistory(false);
    } catch (e) {
      toast.error('Failed to restore: ' + (e.response?.data?.detail || e.message));
    }
  };
  // Single export function — same multi-sheet format as import (channel × category)
  const exportChannelData = async (label = 'Data') => {
    try {
      const r = await api.get('/aop/planning/template', {
        params: { fiscal_year: selectedFiscalYear },
        responseType: 'blob',
      });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(new Blob([r.data]));
      a.download = `AOP_FY${selectedFiscalYear}_${label}.xlsx`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      const isTemplate = label === 'Template';
      toast.success(isTemplate ? `FY${selectedFiscalYear} blank template downloaded` : `FY${selectedFiscalYear} data exported successfully`);
      track(isTemplate ? EVENTS.AOP_TEMPLATE_DOWNLOAD : EVENTS.AOP_EXPORT, { fiscal_year: selectedFiscalYear, label });
    } catch { toast.error('Failed to download'); }
  };

  // exportToExcel removed — exportChannelData handles this now

  // ── Import with preview step ──────────────────────────────────────────────
  const [importStep,    setImportStep]    = useState('upload');  // 'upload' | 'preview' | 'importing'
  const [importPreview, setImportPreview] = useState(null);

  const handlePreview = async () => {
    if (!importFile) { toast.error('Select a file first'); return; }
    setImporting(true);
    try {
      const r = await aopAPI.previewImport(importFile, selectedFiscalYear);
      setImportPreview(r.data);
      setImportStep('preview');
    } catch (e) {
      toast.error('Could not parse file: ' + (e.response?.data?.detail || e.message));
    } finally {
      setImporting(false);
    }
  };

  const handleImport = async () => {
    setImportStep('importing');
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append('file', importFile);
      const r = await api.post(`/aop/planning/import-file?fiscal_year=${selectedFiscalYear}`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const { channels_updated, categories_total, errors, message } = r.data;
      toast.success(message);
      track(EVENTS.AOP_IMPORT_CONFIRM, { channels_updated, categories_total, fiscal_year: selectedFiscalYear, filename: importFile?.name });
      if (errors?.length) errors.forEach(e => toast.warning(e, { duration: 6000 }));
      setShowImportDialog(false);
      setImportFile(null);
      setImportStep('upload');
      setImportPreview(null);
      setDataVersion(v => v + 1);
    } catch (e) {
      toast.error('Import failed: ' + (e.response?.data?.detail || e.message));
      setImportStep('preview');
    } finally {
      setImporting(false);
    }
  };

  const resetImportDialog = () => {
    setShowImportDialog(false);
    setImportFile(null);
    setImportStep('upload');
    setImportPreview(null);
  };

  if (loading) return <div className="space-y-4"><div className="h-8 bg-slate-200 rounded w-60 animate-pulse" /><div className="h-[500px] bg-slate-100 rounded-xl animate-pulse" /></div>;

  return (
    <div className="space-y-4" data-testid="aop-page">

      {/* Single toolbar row: filters left, actions right */}
      <div className="flex items-center justify-between gap-3 bg-white border border-slate-200 rounded-xl px-3 py-2">

        {/* Left — filters */}
        <div className="flex items-center gap-2 flex-wrap">
          {/* Fiscal year selector */}
          <div className="flex items-center gap-1">
            {availableFYs.map(fy => (
              <button
                key={fy}
                onClick={() => setSelectedFiscalYear(fy)}
                className={cn(
                  'px-2.5 py-1 rounded-md text-xs font-semibold transition-colors',
                  selectedFiscalYear === fy
                    ? 'bg-primary-600 text-white'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                )}
              >
                FY{fy}
              </button>
            ))}
            {/* Add new fiscal year */}
            <button
              onClick={() => {
                const nextFY = Math.max(...availableFYs) + 1;
                setAvailableFYs(prev => [...prev, nextFY]);
                setSelectedFiscalYear(nextFY);
              }}
              className="px-2 py-1 rounded-md text-xs font-semibold bg-slate-100 text-slate-400 hover:bg-slate-200 hover:text-slate-600 transition-colors"
              title="Add next fiscal year"
            >
              + FY{Math.max(...availableFYs) + 1}
            </button>
          </div>

          <div className="h-5 w-px bg-slate-200" />

          <div className="flex items-center gap-1.5">
            <Calendar className="w-4 h-4 text-slate-400" />
            <Select value={timePeriod} onValueChange={setTimePeriod}>
              <SelectTrigger className="w-28 h-8 text-sm"><SelectValue /></SelectTrigger>
              <SelectContent>{Object.entries(TIME_PERIODS).map(([k,v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>

          <div className="h-5 w-px bg-slate-200" />

          <div className="flex items-center gap-1.5">
            <Layers className="w-4 h-4 text-slate-400" />
            <SearchableSelect value={selectedChannel} onValueChange={v => { track(EVENTS.AOP_FILTER_CHANNEL, { from: selectedChannel, to: v, fiscal_year: selectedFiscalYear }); setSelectedChannel(v); }} options={dynamicChannelOptions} placeholder="All Channels" className="w-40" />
          </div>

          <div className="flex items-center gap-1.5">
            <BarChart3 className="w-4 h-4 text-slate-400" />
            <SearchableSelect value={selectedCategory} onValueChange={v => { track(EVENTS.AOP_FILTER_CATEGORY, { from: selectedCategory, to: v, fiscal_year: selectedFiscalYear }); setSelectedCategory(v); }} options={dynamicCategoryOptions} placeholder="All Categories" className="w-44" />
          </div>

          {/* Active filter chips */}
          {selectedChannel !== 'all' && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 text-xs font-medium">
              {dynamicChannelOptions.find(c => c.value === selectedChannel)?.label}
              <button onClick={() => setSelectedChannel('all')} className="hover:text-blue-900"><X className="w-3 h-3" /></button>
            </span>
          )}
          {selectedCategory !== 'all' && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-purple-100 text-purple-700 text-xs font-medium">
              {dynamicCategoryOptions.find(c => c.value === selectedCategory)?.label}
              <button onClick={() => setSelectedCategory('all')} className="hover:text-purple-900"><X className="w-3 h-3" /></button>
            </span>
          )}
        </div>

        {/* Right — actions */}
        <div className="flex items-center gap-1.5 flex-shrink-0">
          {/* Versions */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className="text-slate-500 hover:text-slate-700" title="Version history">
                <History className="w-4 h-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => setShowSaveVersion(true)}><Save className="w-4 h-4 mr-2" /> Save Version</DropdownMenuItem>
              <DropdownMenuItem onClick={loadVersions}><History className="w-4 h-4 mr-2" /> View History</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Excel */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className="text-slate-500 hover:text-slate-700" title="Excel import / export">
                <FileSpreadsheet className="w-4 h-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => exportChannelData('Export')}>
                <Download className="w-4 h-4 mr-2" /> Export to Excel
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => exportChannelData('Template')}>
                <Download className="w-4 h-4 mr-2 text-slate-400" /> Download Blank Template
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => setShowImportDialog(true)}>
                <Upload className="w-4 h-4 mr-2 text-slate-400" /> Import from Excel
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Expand / Collapse — icon only */}
          <Button variant="ghost" size="sm" onClick={expandAll}  className="text-slate-500 hover:text-slate-700" title="Expand all"><Plus  className="w-4 h-4" /></Button>
          <Button variant="ghost" size="sm" onClick={collapseAll} className="text-slate-500 hover:text-slate-700" title="Collapse all"><Minus className="w-4 h-4" /></Button>

          <div className="h-5 w-px bg-slate-200" />

          {/* Edit mode toggle */}
          <Button
            variant={editMode ? 'default' : 'outline'}
            size="sm"
            onClick={() => {
              const next = !editMode;
              setEditMode(next);
              track(next ? EVENTS.AOP_EDIT_MODE_ON : EVENTS.AOP_EDIT_MODE_OFF, { fiscal_year: selectedFiscalYear });
            }}
            className={cn(editMode ? 'bg-amber-500 hover:bg-amber-600 border-amber-500 text-white' : 'text-slate-600')}
            title={editMode ? 'Exit edit mode' : 'Enter edit mode to make changes'}
          >
            {editMode ? <><Unlock className="w-3.5 h-3.5 mr-1.5" />Editing</> : <><Lock className="w-3.5 h-3.5 mr-1.5" />Locked</>}
          </Button>

          {/* Save */}
          {lastSaved && <span className="text-xs text-slate-400 whitespace-nowrap">Saved {lastSaved.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}</span>}
          <Button size="sm" onClick={() => handleSave(false)} disabled={saving}>
            <Save className="w-3.5 h-3.5 mr-1.5" />{saving ? 'Saving…' : 'Save'}
          </Button>
        </div>
      </div>

      {/* P&L Table — overflow:auto handles both vertical + horizontal scroll */}
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <div style={{ height: 'calc(100vh - 240px)', overflow: 'auto' }}>
          <table className="border-collapse" style={{ minWidth: 'max-content', width: '100%' }}>
              <thead className="sticky top-0 z-20 bg-slate-100">
                <tr>
                  <th className="px-3 py-2.5 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider sticky left-0 bg-slate-100 z-30 min-w-[260px] border-r border-r-slate-200 shadow-[2px_0_4px_-2px_rgba(0,0,0,0.08)]">Line Item</th>
                  {Array(periodConfig.periods).fill(0).map((_,i) => (
                    <th key={i} className="px-2 py-2.5 text-right text-xs font-semibold text-slate-600 uppercase tracking-wider min-w-[88px] whitespace-nowrap">{periodConfig.format(i)}</th>
                  ))}
                  <th className="px-2 py-2.5 text-right text-xs font-semibold text-slate-600 uppercase bg-slate-200 border-l border-slate-300 min-w-[100px]">FY Total</th>
                </tr>
              </thead>
              <tbody>{PNL_STRUCTURE.map(item => renderRow(item))}</tbody>
            </table>
          </div>
      </div>

      {/* KPI strip */}
      <div className="grid grid-cols-5 gap-3">
        {/* KPI strip */}
      {(() => {
          const nr    = getRowTotal('net_revenue');
          const gm    = getRowTotal('gross_margin');
          const cm1   = getRowTotal('cm1');
          const cm2   = getRowTotal('cm2');
          const ebitda = getRowTotal('ebitda');
          const pct = (v) => nr ? `${((v/nr)*100).toFixed(1)}% of Net` : '';
          return [
            { label: 'Net Sales',    val: nr,     color: 'text-slate-900' },
            { label: 'Gross Margin', val: gm,     color: 'text-emerald-700', sub: pct(gm) },
            { label: 'CM1',          val: cm1,    color: 'text-blue-700',    sub: pct(cm1) },
            { label: 'CM2',          val: cm2,    color: 'text-primary-900', sub: pct(cm2), bg: 'bg-primary-50 border-primary-200' },
            { label: 'EBITDA',       val: ebitda, color: ebitda >= 0 ? 'text-emerald-700' : 'text-red-600', sub: pct(ebitda), bg: ebitda >= 0 ? 'bg-emerald-50 border-emerald-200' : 'bg-red-50 border-red-200' },
          ].map(({ label, val, color, sub, bg }) => (
            <div key={label} className={cn('border rounded-xl p-3', bg || 'bg-white border-slate-200')}>
              <p className="text-xs text-slate-500 mb-0.5">{label}</p>
              <p className={cn('font-mono text-lg font-bold', color, val < 0 && 'text-red-600')}>{fmt(val)}</p>
              {sub && <p className="text-xs text-slate-400 mt-0.5">{sub}</p>}
            </div>
          ));
        })()}
      </div>

      {/* Save Version dialog */}
      <Dialog open={showSaveVersion} onOpenChange={setShowSaveVersion}>
        <DialogContent>
          <DialogHeader><DialogTitle>Save Version</DialogTitle><DialogDescription>Snapshot the current plan.</DialogDescription></DialogHeader>
          <div className="py-4"><Label>Description</Label><Textarea value={versionDescription} onChange={e => setVersionDescription(e.target.value)} className="mt-1" placeholder="e.g., Post Q1 review" /></div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSaveVersion(false)}>Cancel</Button>
            <Button onClick={saveVersion}><Save className="w-4 h-4 mr-2" /> Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Version History dialog */}
      <Dialog open={showVersionHistory} onOpenChange={setShowVersionHistory}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>Version History</DialogTitle></DialogHeader>
          <div className="py-4">
            {loadingVersions ? <div className="flex justify-center py-10"><RefreshCw className="animate-spin text-slate-400" /></div>
              : versions.length === 0 ? <p className="text-center text-slate-500 py-8">No versions saved yet.</p>
              : <div className="space-y-2 max-h-96 overflow-y-auto">
                {versions.map(v => (
                  <div key={v.id} className={cn('flex items-center justify-between p-3 rounded-lg border cursor-pointer', selectedVersions.includes(v.id) ? 'border-primary-500 bg-primary-50' : 'border-slate-200 hover:bg-slate-50')}
                    onClick={() => setSelectedVersions(prev => prev.includes(v.id) ? prev.filter(x=>x!==v.id) : prev.length>=2 ? [prev[1],v.id] : [...prev,v.id])}>
                    <div>
                      <div className="flex items-center gap-2"><span className="font-medium">v{v.version_number}</span><span className="text-sm text-slate-500">{v.description}</span></div>
                      <div className="text-xs text-slate-400">{v.created_by_name} · {new Date(v.created_at).toLocaleString()}</div>
                    </div>
                    <Button variant="ghost" size="sm" onClick={e=>{e.stopPropagation();restoreVersion(v);}}><RotateCcw className="w-4 h-4 mr-1" />Restore</Button>
                  </div>
                ))}
              </div>}
          </div>
          <DialogFooter><Button variant="outline" onClick={()=>setShowVersionHistory(false)}>Close</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Import dialog — 3 steps: upload → preview → importing */}
      <Dialog open={showImportDialog} onOpenChange={resetImportDialog}>
        <DialogContent className={cn(importStep === 'preview' ? 'max-w-3xl' : 'max-w-lg')}>

          {/* Step 1: Upload */}
          {importStep === 'upload' && (
            <>
              <DialogHeader>
                <DialogTitle>Import from Excel</DialogTitle>
                <DialogDescription>
                  Upload the multi-sheet AOP file (same format as the exported template).
                  A preview will show before any data is saved.
                </DialogDescription>
              </DialogHeader>
              <div className="py-4">
                <div className="flex items-center justify-center p-8 border-2 border-dashed border-slate-200 rounded-xl bg-slate-50 hover:border-primary-300 transition-colors">
                  <div className="text-center">
                    <FileSpreadsheet className="w-10 h-10 mx-auto text-slate-400 mb-2" />
                    <input type="file" accept=".xlsx,.xls" onChange={e => setImportFile(e.target.files?.[0] || null)} className="hidden" id="excel-upload" />
                    <label htmlFor="excel-upload" className="cursor-pointer text-primary-600 hover:text-primary-700 font-medium">
                      Click to select file
                    </label>
                    <p className="text-xs text-slate-400 mt-1">.xlsx or .xls · one sheet per channel</p>
                    {importFile && (
                      <div className="mt-3 flex items-center gap-2 text-sm text-slate-700 justify-center bg-white border border-slate-200 rounded-lg px-3 py-2">
                        <Check className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                        <span className="truncate max-w-xs">{importFile.name}</span>
                        <button onClick={() => setImportFile(null)} className="text-slate-300 hover:text-slate-500 flex-shrink-0"><X className="w-4 h-4" /></button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={resetImportDialog}>Cancel</Button>
                <Button onClick={handlePreview} disabled={!importFile || importing}>
                  {importing
                    ? <><RefreshCw className="w-4 h-4 mr-2 animate-spin" />Parsing…</>
                    : <><Check className="w-4 h-4 mr-2" />Preview Data</>}
                </Button>
              </DialogFooter>
            </>
          )}

          {/* Step 2: Preview */}
          {importStep === 'preview' && importPreview && (
            <>
              <DialogHeader>
                <DialogTitle>Review Before Import</DialogTitle>
                <DialogDescription>
                  {importPreview.filename} · FY{importPreview.fiscal_year} ·
                  {importPreview.channels_found.length} channels · verify totals before confirming
                </DialogDescription>
              </DialogHeader>

              <div className="py-2 space-y-4 max-h-[60vh] overflow-y-auto pr-1">

                {/* Grand totals */}
                <div className="grid grid-cols-4 gap-2">
                  {[
                    { label: 'Gross Sales',  value: importPreview.grand_totals.gross_sales,  color: 'text-slate-900' },
                    { label: 'Net Sales',    value: importPreview.grand_totals.net_sales,    color: 'text-slate-900' },
                    { label: 'Gross Margin', value: importPreview.grand_totals.gross_margin, color: 'text-emerald-700' },
                    { label: 'GM %',         value: `${importPreview.grand_totals.gm_pct}%`, color: 'text-emerald-700', raw: true },
                  ].map(({ label, value, color, raw }) => (
                    <div key={label} className="bg-slate-50 border border-slate-200 rounded-lg p-3">
                      <p className="text-xs text-slate-400 mb-0.5">{label}</p>
                      <p className={cn('text-sm font-bold font-mono', color)}>
                        {raw ? value : `₹${value.toLocaleString('en-IN')}L`}
                      </p>
                    </div>
                  ))}
                </div>

                {/* Warnings */}
                {importPreview.warnings.length > 0 && (
                  <div className="space-y-1">
                    {importPreview.warnings.map((w, i) => (
                      <div key={i} className="flex items-start gap-2 px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-700">
                        <span className="flex-shrink-0 mt-0.5">⚠️</span>
                        <span>{w}</span>
                      </div>
                    ))}
                  </div>
                )}

                {/* Channel breakdown table */}
                <div className="rounded-xl border border-slate-200 overflow-hidden">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-slate-100 border-b border-slate-200">
                        {['Channel','Categories','Gross Sales (L)','Net Sales (L)','Gross Margin (L)','GM %'].map(h => (
                          <th key={h} className="text-left px-3 py-2 text-xs font-semibold text-slate-500 uppercase tracking-wider">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {importPreview.channel_summaries.map(ch => (
                        <tr key={ch.sheet} className="hover:bg-slate-50">
                          <td className="px-3 py-2 font-medium text-slate-800">
                            {ch.sheet}
                            {ch.is_new && <span className="ml-1.5 px-1.5 py-0.5 rounded-full bg-blue-100 text-blue-700 text-xs">New</span>}
                          </td>
                          <td className="px-3 py-2 text-slate-500">{ch.categories}</td>
                          <td className="px-3 py-2 font-mono text-slate-700">{ch.gross_sales.toLocaleString('en-IN')}</td>
                          <td className="px-3 py-2 font-mono text-slate-700">{ch.net_sales.toLocaleString('en-IN')}</td>
                          <td className="px-3 py-2 font-mono text-emerald-700">{ch.gross_margin.toLocaleString('en-IN')}</td>
                          <td className="px-3 py-2 font-mono text-emerald-700">{ch.gm_pct}%</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Missing channels note */}
                {importPreview.channels_missing.length > 0 && (
                  <p className="text-xs text-slate-400 px-1">
                    Not in file (existing DB data preserved): {importPreview.channels_missing.join(', ')}
                  </p>
                )}
              </div>

              <DialogFooter className="pt-2 border-t border-slate-100">
                <Button variant="outline" onClick={() => setImportStep('upload')}>← Back</Button>
                <Button
                  onClick={handleImport}
                  disabled={!importPreview.can_import}
                  className="bg-emerald-600 hover:bg-emerald-700"
                >
                  <Upload className="w-4 h-4 mr-2" />
                  Confirm Import ({importPreview.channels_found.length} channels)
                </Button>
              </DialogFooter>
            </>
          )}

          {/* Step 3: Importing */}
          {importStep === 'importing' && (
            <div className="py-12 flex flex-col items-center gap-4 text-center">
              <RefreshCw className="w-8 h-8 text-primary-600 animate-spin" />
              <p className="font-medium text-slate-800">Importing data…</p>
              <p className="text-sm text-slate-500">Updating {importPreview?.channels_found?.length} channels in the database</p>
            </div>
          )}

        </DialogContent>
      </Dialog>
    </div>
  );
}
